/**
 * useFrancLanguageDetection.ts
 * React hook for detecting text language using franc-all.
 */

import { franc, francAll } from 'franc-all';
import { useTypedSelector } from '@fiji/common/src/app/store';
import { selectCurrentPreferredLanguage } from '@fiji/common/src/features/profile/profileSlice';
export const SUPPORTED_LANGS: Record<string, string> = {
    eng: 'en-US', //English language
    fra: 'fr-FR', // French language
    deu: 'de-DE', // German language
    ita: 'it-IT', // Italian language
    spa: 'es-ES', // Spanish language
    por: 'pt-PT', // Portuguese language
    nld: 'nl-NL', // Dutch language
    nor: 'nb-NO', // Norwegian
};

export type SafeDetectionResult = {
    detectedLanguageCode: string;
    mappedLocale: string;
    error?: string;
};

type UseFrancLanguageDetectionReturn = {
    detectLanguageCode: (text: string) => string;
    detectMappedLocale: (text: string) => string;
    safeDetectLanguage: (text: string) => SafeDetectionResult; // ⭐ NEW
    lazyDetectMappedLocale: (text: string) => Promise<string>;
};

export const useFrancLanguageDetection = (): UseFrancLanguageDetectionReturn => {
    const allowedLanguagesResponse = useTypedSelector(selectCurrentPreferredLanguage);
    const allowedLanguages = allowedLanguagesResponse?.preferredLanguage?.data ?? [];
    const detectLanguageCode = (text: string): string => {
        if (!text || typeof text !== 'string') {
            return '';
        }

        const trimmed = text.trim();
        if (trimmed.length === 0) {
            return '';
        }

        // Count characters including spaces
        const charCount = text.length;

        // Don't guess language until user has typed at least 10 characters
        if (charCount < 10) {
            return '';
        }

        const code = franc(trimmed);
        return code === 'und' ? '' : code;
    };

    const detectMappedLocale = (text: string): string => {
        const code = detectLanguageCode(text);
        return code ? SUPPORTED_LANGS[code] ?? '' : '';
    };

    const safeDetectLanguage = (text: string): SafeDetectionResult => {
        try {
            if (!text || text.trim().length === 0) {
                return {
                    detectedLanguageCode: '',
                    mappedLocale: '',
                    error: '',
                };
            }

            const trimmedText = text.trim();
            // Count characters including spaces
            const charCount = text.length;
            // Don't guess language until user has typed at least 10 characters
            if (charCount < 10) {
                return {
                    detectedLanguageCode: '',
                    mappedLocale: '',
                    error: '',
                };
            }

            // Get all possible language detections with confidence scores
            const allDetections = francAll(trimmedText, { minLength: 3 }).slice(0, 3);
            if (allDetections.length === 0 || allDetections[0][0] === 'und') {
                return {
                    detectedLanguageCode: '',
                    mappedLocale: '',
                    error: '',
                };
            }

            // Convert user's preferred locales (e.g., 'de-DE') to franc codes (e.g., 'deu')
            const preferredCodes = allowedLanguages
                .map((locale: string) => Object.keys(SUPPORTED_LANGS).find((key) => SUPPORTED_LANGS[key] === locale))
                .filter(Boolean) as string[];

            // Filter detections to only include supported languages
            const supportedDetections = allDetections.filter(([code]) => Object.keys(SUPPORTED_LANGS).includes(code));

            // Check if all allowed languages are en-US (skip validation in that case)
            const isAllEnglish =
                allowedLanguages.length > 0 && allowedLanguages.every((lang: string) => lang === 'en-US');

            // Skip all error validations if all allowed languages are en-US
            if (isAllEnglish) {
                const detectedCode = supportedDetections.length > 0 ? supportedDetections[0][0] : allDetections[0][0];
                const mappedLocale = SUPPORTED_LANGS[detectedCode] || '';
                return {
                    detectedLanguageCode: detectedCode,
                    mappedLocale,
                    error: '',
                };
            }

            // If no supported languages detected
            if (supportedDetections.length === 0) {
                return {
                    detectedLanguageCode: allDetections[0][0],
                    mappedLocale: '',
                    error: `Detected language '${allDetections[0][0]}' is not supported by application.`,
                };
            }

            // Check if any preferred language is in supported detections with reasonable confidence
            const CONFIDENCE_THRESHOLD = 0.5; // Lowered threshold to catch ambiguous cases
            const preferredMatch = supportedDetections.find(
                ([code, confidence]) => preferredCodes.includes(code) && confidence > CONFIDENCE_THRESHOLD
            );

            // Use preferred match if found, otherwise use top supported detection
            const [detectedCode] = preferredMatch || supportedDetections[0];

            const mappedLocale = SUPPORTED_LANGS[detectedCode];

            if (!mappedLocale) {
                return {
                    detectedLanguageCode: detectedCode,
                    mappedLocale: '',
                    error: `Language code '${detectedCode}' not supported by application.`,
                };
            }

            // Check allowed languages from backend
            if (allowedLanguages && allowedLanguages.length > 0 && !allowedLanguages.includes(mappedLocale)) {
                return {
                    detectedLanguageCode: detectedCode,
                    mappedLocale,
                    error: `Please input text in your selected language: ${allowedLanguages.join(', ')}`,
                };
            }

            return {
                detectedLanguageCode: detectedCode,
                mappedLocale,
            };
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Language detection failed.';
            return {
                detectedLanguageCode: '',
                mappedLocale: '',
                error: message,
            };
        }
    };

    const lazyDetectMappedLocale = async (text: string): Promise<string> => {
        const trimmed = typeof text === 'string' ? text.trim() : '';

        if (trimmed.length === 0) {
            return '';
        }

        const module = await import('franc-all');
        const detected = module.franc(trimmed);

        if (!detected || detected === 'und') {
            return '';
        }

        return SUPPORTED_LANGS[detected] ?? '';
    };

    return {
        detectLanguageCode,
        detectMappedLocale,
        safeDetectLanguage,
        lazyDetectMappedLocale,
    };
};
